import type { OpenTab } from "@/components/features/document-tabs"
import { compactSearchMatch } from "@/lib/compact-search-match"

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const DOC_TAB_TYPES = new Set<OpenTab["type"]>(["work-spec", "note", "voice-memo"])

const FAVORITES_KEY = "flownotes.paletteFavoritePaths.v1"
const OPEN_STATS_KEY = "flownotes.paletteOpenStats.v1"

/** Keep in sync with FAVORITE_BOOST in this file (UI re-sort on toggle). */
export const PALETTE_FAVORITE_MATCH_BOOST = 10_000

type OpenStat = { opens: number; lastAt: number }

function basenameNoExt(filePath: string): string {
  const base = filePath.split("/").pop() ?? filePath
  return base.replace(/\.(md|markdown)$/i, "").toLowerCase()
}

/** UUID tab ids for POST /api/documents/search openTabIds (server boost). */
export function extractOpenDocumentIdsForSearch(tabs: OpenTab[]): string[] {
  const ids = tabs
    .filter((t) => DOC_TAB_TYPES.has(t.type) && UUID_RE.test(t.id))
    .map((t) => t.id)
  return [...new Set(ids)]
}

/** Open document paths for client-side boost when tab id is a path, not UUID. */
export function extractOpenFilePathsForSearch(tabs: OpenTab[]): string[] {
  const paths: string[] = []
  for (const t of tabs) {
    if (!DOC_TAB_TYPES.has(t.type)) continue
    if (t.filePath) paths.push(t.filePath)
    else if (t.id.includes("/")) paths.push(t.id)
  }
  return [...new Set(paths)]
}

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

function saveJson(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* ignore quota */
  }
}

export function getPaletteFavoritePaths(): Set<string> {
  const list = loadJson<string[]>(FAVORITES_KEY, [])
  return new Set(list)
}

export function isPaletteFavoritePath(filePath: string | undefined): boolean {
  if (!filePath) return false
  return getPaletteFavoritePaths().has(filePath)
}

/** Toggle favorite; returns whether path is now favorited. */
export function togglePaletteFavoritePath(filePath: string): boolean {
  const set = getPaletteFavoritePaths()
  if (set.has(filePath)) {
    set.delete(filePath)
  } else {
    set.add(filePath)
  }
  saveJson(FAVORITES_KEY, [...set])
  return set.has(filePath)
}

export function recordPaletteDocumentOpen(documentId: string, filePath?: string) {
  const key = filePath || documentId
  if (!key) return
  const stats = loadJson<Record<string, OpenStat>>(OPEN_STATS_KEY, {})
  const prev = stats[key] || { opens: 0, lastAt: 0 }
  stats[key] = { opens: prev.opens + 1, lastAt: Date.now() }
  saveJson(OPEN_STATS_KEY, stats)
}

function openFrequencyBoost(documentId: string, filePath?: string): number {
  const stats = loadJson<Record<string, OpenStat>>(OPEN_STATS_KEY, {})
  const s = (filePath && stats[filePath]) || stats[documentId]
  if (!s?.opens) return 0
  // Log-scaled; caps around 320 for very frequent docs
  return Math.min(320, Math.round(Math.log2(s.opens + 1) * 80))
}

const EXACT_BASENAME = 12_000
const BASENAME_STARTS = 6_000
const BASENAME_CONTAINS = 3_000
const TITLE_EXACT = 900
const TITLE_STARTS = 450
const TITLE_CONTAINS = 220
const OPEN_PATH_BOOST = 900
const FAVORITE_BOOST = PALETTE_FAVORITE_MATCH_BOOST

/**
 * Filename/title alignment with query (normalized, no tag:/file: prefix handling - caller should skip structured queries).
 *
 * Compact match: dashes/underscores/spaces in the haystack are ignored so
 * "setupsou" matches "setup-soul" (same idea as Cursor CMD+P).
 */
export function computeFilenameTitleBoost(
  queryLower: string,
  filePath: string | undefined,
  title: string | undefined,
): number {
  if (!queryLower) return 0
  let boost = 0
  const q = queryLower.replace(/\.(md|markdown)$/i, "")

  if (filePath) {
    const base = basenameNoExt(filePath)
    if (base === q) boost += EXACT_BASENAME
    else if (base.startsWith(q)) boost += BASENAME_STARTS
    else if (q.length >= 2 && compactSearchMatch(base, q)) {
      boost += BASENAME_CONTAINS - Math.min(1_200, base.length * 12)
    }
  }

  const t = (title || "").toLowerCase()
  if (t) {
    if (t === q) boost += TITLE_EXACT
    else if (t.startsWith(q)) boost += TITLE_STARTS
    else if (q.length >= 2 && compactSearchMatch(t, q)) boost += TITLE_CONTAINS
  }

  return boost
}

export function computePalettePersonalBoost(
  documentId: string,
  filePath: string | undefined,
  _openTabIds: string[],
  openFilePaths: string[],
): number {
  let boost = openFrequencyBoost(documentId, filePath)

  if (filePath && isPaletteFavoritePath(filePath)) {
    boost += FAVORITE_BOOST
  }

  // UUID open tabs are boosted server-side via openTabIds; path-only tabs need a client bump
  if (filePath) {
    const openPaths = new Set(openFilePaths)
    if (openPaths.has(filePath)) boost += OPEN_PATH_BOOST
  }

  return boost
}

